import { useQueryClient } from "@tanstack/react-query";
import { ScaleIcon } from "lucide-react";
import { useRef, useState } from "react";
import { useNavigate } from "react-router";
import type { SessionDetail } from "../../../shared/api.ts";
import { Composer } from "@/components/session/composer.tsx";
import { ClassifyEmptyState } from "@/components/session/empty-state.tsx";
import { ModelAccessNotice } from "@/components/session/model-access-notice.tsx";
import { applySendResponse } from "@/components/session/session-view.tsx";
import { NavigationButton } from "@/components/shell/shell-context.tsx";
import { useI18n } from "@/i18n/index.tsx";
import { api, queryKeys } from "@/lib/api.ts";
import { useIsMobile, useIsWide } from "@/lib/hooks.ts";
import { useDocumentTitle, useModelAccess } from "@/lib/queries.ts";

/** `/app`: the empty state and the composer. Sending (or attaching) creates the session first. */
export function NewSessionPage() {
  const { t, language } = useI18n();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isWide = useIsWide();
  const isMobile = useIsMobile();
  const [draft, setDraft] = useState("");
  const [focusKey, setFocusKey] = useState(0);
  const pending = useRef<Promise<string> | null>(null);
  // Without model access the box is blocked, so no empty session is created for a message that can't run.
  const access = useModelAccess();
  const noAccess = access.data?.effective === null;
  useDocumentTitle(t.shell.newClassification, t.brand.name);

  const ensureSession = () => {
    if (!pending.current) {
      pending.current = api
        .createSession()
        .then((session) => session.id)
        .catch((error: unknown) => {
          pending.current = null;
          throw error;
        });
    }
    return pending.current;
  };

  const onSend = async (text: string, attachmentIds: string[]) => {
    const sessionId = await ensureSession();
    const response = await api.sendMessage(sessionId, {
      text,
      language,
      attachmentIds: attachmentIds.length > 0 ? attachmentIds : undefined,
    });
    // Open the session with the message already in place; the page refetches it.
    const detail = await api.session(sessionId).catch(() => null);
    if (detail) {
      const known = detail.messages.some((message) => message.id === response.message.id);
      queryClient.setQueryData<SessionDetail>(queryKeys.session(sessionId), known ? detail : applySendResponse(detail, response));
    }
    void queryClient.invalidateQueries({ queryKey: queryKeys.sessions });
    setDraft("");
    void navigate(`/app/s/${sessionId}`);
  };

  return (
    <div className="flex h-full min-h-0">
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center gap-2 border-b px-2.5 sm:px-4">
          <NavigationButton />
          <h1 className="truncate px-1.5 text-[15px] font-bold tracking-[-0.01em]">{t.shell.newClassification}</h1>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <ClassifyEmptyState
            onExample={(text) => {
              setDraft(text);
              setFocusKey((key) => key + 1);
            }}
          />
        </div>
        <div className="shrink-0 px-3 pt-1 pb-3 sm:px-6 sm:pb-4">
          <div className="mx-auto flex w-full max-w-3xl flex-col gap-2">
            {noAccess && access.data ? <ModelAccessNotice view={access.data} /> : null}
            <Composer
              value={draft}
              onChange={setDraft}
              ensureSession={ensureSession}
              onSend={onSend}
              block={noAccess ? "noAccess" : null}
              placeholder={t.composer.placeholder}
              autoFocus={!isMobile}
              focusKey={focusKey}
            />
          </div>
        </div>
      </div>
      {isWide ? (
        <aside aria-label={t.session.panel} className="flex w-[400px] shrink-0 flex-col border-l bg-panel">
          <div className="flex h-11 shrink-0 items-center border-b px-5 text-[13px] font-semibold">{t.panel.tabs.classification}</div>
          <div className="flex flex-col items-center gap-3 px-8 py-16 text-center">
            <span className="flex size-11 items-center justify-center rounded-full bg-muted text-muted-foreground">
              <ScaleIcon className="size-5" aria-hidden />
            </span>
            <p className="max-w-[18rem] text-[13px] leading-relaxed text-muted-foreground">{t.panel.empty}</p>
          </div>
        </aside>
      ) : null}
    </div>
  );
}
